import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Building2,
  Crown,
  Users,
  Home,
  DoorOpen,
  DollarSign,
  Ban,
  CheckCircle,
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const AdminOrganisations = () => {
  const { t } = useI18n();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadOrgs(); }, []);

  const loadOrgs = async () => {
    try {
      const res = await adminAPI.getOrganisations();
      setOrgs(res.data);
    } catch (error) {
      toast.error('Failed to load organisations');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async (orgId) => {
    try {
      await adminAPI.suspendOrg(orgId);
      toast.success('Organisation suspended');
      loadOrgs();
    } catch (error) {
      toast.error('Failed to suspend organisation');
    }
  };

  const handleActivate = async (orgId) => {
    try {
      await adminAPI.activateOrg(orgId);
      toast.success('Organisation activated');
      loadOrgs();
    } catch (error) {
      toast.error('Failed to activate organisation');
    }
  };

  const handleTierChange = async (orgId, tier) => {
    try {
      await adminAPI.updateOrgSubscription(orgId, tier, 'active');
      toast.success('Subscription updated');
      loadOrgs();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'ENTERPRISE': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'EXPERT': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'PRO': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'trial': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'suspended': return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'past_due': return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
      case 'canceled': return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  if (loading) {
    return (
      <DashboardLayout type="admin" title={t('admin.organisations')}>
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-36 bg-card animate-pulse" />)}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="admin" title={t('admin.organisations')}>
      <div className="space-y-6" data-testid="admin-organisations-page">
        <p className="text-muted-foreground">Manage organisations, subscriptions, and account status</p>

        {/* Summary Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Orgs</p>
              <p className="text-2xl font-semibold mt-1">{orgs.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Active</p>
              <p className="text-2xl font-semibold text-emerald-400 mt-1">
                {orgs.filter(o => o.subscription_status === 'active' || o.subscription_status === 'trial').length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Suspended</p>
              <p className="text-2xl font-semibold text-red-400 mt-1">
                {orgs.filter(o => o.is_suspended).length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Revenue</p>
              <p className="text-2xl font-semibold text-gold mt-1">
                ${orgs.reduce((sum, o) => sum + (o.total_revenue || 0), 0).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Org List */}
        {orgs.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">No Organisations Yet</h3>
              <p className="text-muted-foreground">Organisations appear when landlords register</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orgs.map((org, index) => (
              <Card
                key={org.id}
                className={`bg-card border-border hover:border-gold/30 transition-all duration-300 card-animate ${org.is_suspended ? 'opacity-70' : ''}`}
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`org-card-${org.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Org Info */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-12 h-12 bg-gold/10 flex items-center justify-center shrink-0">
                        <Building2 className="w-6 h-6 text-gold" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-medium text-lg">{org.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          Owner: {org.owner_name} ({org.owner_email})
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className={`px-2 py-0.5 text-xs uppercase tracking-wider border ${getTierColor(org.subscription_tier || 'BASIC')}`}>
                            {org.subscription_tier || 'BASIC'}
                          </span>
                          <span className={`px-2 py-0.5 text-xs uppercase tracking-wider border ${getStatusColor(org.is_suspended ? 'suspended' : (org.subscription_status || 'trial'))}`}>
                            {org.is_suspended ? 'SUSPENDED' : (org.subscription_status || 'trial')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4 text-center shrink-0">
                      <div>
                        <Home className="w-4 h-4 mx-auto text-muted-foreground mb-1" />
                        <p className="text-lg font-semibold">{org.property_count}</p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Properties</p>
                      </div>
                      <div>
                        <DoorOpen className="w-4 h-4 mx-auto text-muted-foreground mb-1" />
                        <p className="text-lg font-semibold">{org.unit_count}</p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Units</p>
                      </div>
                      <div>
                        <Users className="w-4 h-4 mx-auto text-muted-foreground mb-1" />
                        <p className="text-lg font-semibold">{org.tenant_count}</p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Tenants</p>
                      </div>
                      <div>
                        <DollarSign className="w-4 h-4 mx-auto text-muted-foreground mb-1" />
                        <p className="text-lg font-semibold">${(org.total_revenue || 0).toLocaleString()}</p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Revenue</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Select
                        value={org.subscription_tier || 'BASIC'}
                        onValueChange={(tier) => handleTierChange(org.id, tier)}
                      >
                        <SelectTrigger className="w-32 bg-background border-[#e5e7eb]" data-testid={`org-tier-select-${org.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BASIC">Basic</SelectItem>
                          <SelectItem value="PRO">Pro</SelectItem>
                          <SelectItem value="EXPERT">Expert</SelectItem>
                          <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                      {org.is_suspended ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                          onClick={() => handleActivate(org.id)}
                          data-testid={`activate-org-${org.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Activate
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          onClick={() => handleSuspend(org.id)}
                          data-testid={`suspend-org-${org.id}`}
                        >
                          <Ban className="w-4 h-4 mr-1" /> Suspend
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminOrganisations;
